import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import debounce from '../../../helpers/debounce';

import { useWindowSize } from '../../../hooks/useWindowSize';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';

import { ButtonIcon } from '../../../design-system';
import Loading from '../../../components/Loading';
import ArrowLeftSLineIcon from '../../../design-system/Icons/ArrowLeftSLineIcon';
import ArrowRightSLineIcon from '../../../design-system/Icons/ArrowRightSLineIcon';
import PlaybookTemplateCard from '../PlaybookTemplateCard/PlaybookTemplateCard';

export const REGULAR_CARD_WIDTH = 340;
export const SMALL_CARD_WIDTH = 308;
const CARD_GAP = 20;

const PlaybookTemplatesList = ({
    total,
    filteredTemplates,
    isSearchQuery,
    fetchNextPage,
    canLoadMoreTemplates,
    isLoading,
    isFirstPageLoading,
}) => {
    const scrollContainerRef = useRef(null);

    const [scrollLeft, setScrollLeft] = useState(0);
    const [containerWidth, setContainerWidth] = useState(0);

    const { width: screenWidth } = useWindowSize();

    const cardWidth = screenWidth > 375 ? REGULAR_CARD_WIDTH : SMALL_CARD_WIDTH;
    const fullCardWidth = cardWidth + CARD_GAP;

    const start = Math.max(roundWithHalfDown(scrollLeft / fullCardWidth + 1), 1);
    const end = Math.min(roundWithHalfDown((scrollLeft + containerWidth) / fullCardWidth), total);

    const debouncedScrollHandler = useRef(debounce(scrollHandler, 150))?.current;
    const debouncedWidthChangeHandler = useRef(debounce(handleScreenWidthChange, 150))?.current;

    const observerOptions = {
        root: scrollContainerRef?.current,
        rootMargin: '760px',
        threshold: 0,
    };

    const { guardRef } = useIntersectionObserver({
        handleFetch: fetchNextPage,
        options: filteredTemplates,
        optionsLoading: isLoading,
        canLoadMore: canLoadMoreTemplates,
        observerOptions,
    });

    function scrollHandler() {
        const el = scrollContainerRef.current;
        if (!el) return;

        setScrollLeft(el.scrollLeft);
    }

    function handleScreenWidthChange() {
        const containerWidth = scrollContainerRef.current?.clientWidth ?? 0;
        setContainerWidth(containerWidth);
    }

    useEffect(() => {
        debouncedWidthChangeHandler?.();
    }, [screenWidth]);

    function roundWithHalfDown(n) {
        const fractional = n % 1;
        if (fractional <= 0.5) {
            return Math.floor(n);
        } else {
            return Math.ceil(n);
        }
    }

    const handleScroll = (direction) => {
        if (!scrollContainerRef.current) return;

        const scrollAmount = fullCardWidth;
        scrollContainerRef.current.scrollBy({
            left: direction === 'right' ? scrollAmount : -scrollAmount,
            behavior: 'smooth',
        });
    };

    const leftButtonState = scrollLeft > 4 ? 'default' : 'disabled';

    const areTemplates = !!filteredTemplates?.length || canLoadMoreTemplates;

    const controlsClassNames = classNames(
        'flex flex-col-reverse min-[430px]:flex-row items-center gap-x-2 xs:gap-x-4 gap-y-2',
        {
            'opacity-0 pointer-events-none': !areTemplates,
            'opacity-100': areTemplates,
        }
    );
    const numbersClassNames = classNames(
        'font-body text-body-regular-s text-black whitespace-nowrap',
        {
            'opacity-0': isSearchQuery,
            'opacity-100': !isSearchQuery,
        }
    );

    return (
        <div className="py-2.5 md:pl-2 lg:pl-4 2xl:pl-8 flex flex-col gap-5">
            <div className="flex justify-between items-center gap-2 xs:gap-3 md:pr-2 lg:pr-4 2xl:pr-8">
                <p className="font-heading-bold text-heading-bold-m text-black">Browse Templates</p>

                <div className={controlsClassNames}>
                    <p className={numbersClassNames}>
                        {screenWidth > 700 ? `${start}-${end}` : start} of {total}
                    </p>

                    <div className="flex items-center gap-1">
                        <ButtonIcon
                            type="ghost"
                            size="xs"
                            icon={ArrowLeftSLineIcon}
                            state={leftButtonState}
                            onClick={() => handleScroll('left')}
                        />
                        <ButtonIcon
                            type="ghost"
                            size="xs"
                            icon={ArrowRightSLineIcon}
                            onClick={() => handleScroll('right')}
                        />
                    </div>
                </div>
            </div>

            <div
                className="flex overflow-x-auto pb-2"
                style={{ gap: CARD_GAP }}
                ref={scrollContainerRef}
                onScroll={() => debouncedScrollHandler?.()}
            >
                <div className="flex"></div>
                {areTemplates && (
                    <>
                        {filteredTemplates.map((template) => (
                            <PlaybookTemplateCard
                                key={template.id}
                                template={template}
                                cardWidth={cardWidth}
                            />
                        ))}

                        <div ref={guardRef} style={{ marginLeft: `-${CARD_GAP}px` }}></div>

                        {canLoadMoreTemplates && (
                            <div className="w-[68px] h-[530px] max-w-[68px] min-w-[68px] flex items-center justify-center">
                                <Loading withText={false} />
                            </div>
                        )}
                    </>
                )}
                {!areTemplates && !isFirstPageLoading && (
                    <p className="font-body text-body-bold-s text-neutral-300">
                        No templates found
                    </p>
                )}
                {!areTemplates && isFirstPageLoading && (
                    <div className="w-full h-[420px] flex items-center justify-center">
                        <Loading withText={false} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default PlaybookTemplatesList;
